import React from "react"

import { Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { type ConfigFormValues } from "@/lib/configForm"

import NumberField from "./NumberField"

const LABEL = "text-sm font-medium text-palette-jungleGreen700"

interface GlobalConfigTabProps {
  configForm: ConfigFormValues
  setConfigForm: (form: ConfigFormValues) => void
  hasUnsavedChanges: boolean
  onSave: () => void
  isSaving: boolean
}

const GlobalConfigTab: React.FC<GlobalConfigTabProps> = ({
  configForm,
  setConfigForm,
  hasUnsavedChanges,
  onSave,
  isSaving,
}) => {
  const ratios = [
    {
      id: "ratio-sort-bag",
      label: "Sort bag",
      value: configForm.uk_pallet_ratio_sort_bag,
      key: "uk_pallet_ratio_sort_bag" as const,
    },
    {
      id: "ratio-cage",
      label: "Cage",
      value: configForm.uk_pallet_ratio_cage,
      key: "uk_pallet_ratio_cage" as const,
    },
    {
      id: "ratio-magnum",
      label: "Magnum",
      value: configForm.uk_pallet_ratio_magnum,
      key: "uk_pallet_ratio_magnum" as const,
    },
    {
      id: "ratio-eur-pallet",
      label: "EUR pallet",
      value: configForm.uk_pallet_ratio_eur_pallet,
      key: "uk_pallet_ratio_eur_pallet" as const,
    },
    {
      id: "ratio-uk-pallet",
      label: "UK pallet",
      value: configForm.uk_pallet_ratio_uk_pallet,
      key: "uk_pallet_ratio_uk_pallet" as const,
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-palette-jungleGreen900">
            Global Configuration
          </h2>
          <p className="text-sm text-palette-jungleGreen500 mt-0.5">
            Defaults used for route costs and load factor
          </p>
        </div>
        <Button
          onClick={onSave}
          disabled={!hasUnsavedChanges || isSaving}
          className="gap-2 shrink-0"
        >
          {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
          Save changes
        </Button>
      </div>
      {hasUnsavedChanges && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          You have unsaved changes
        </p>
      )}

      <div className="rounded-xl border border-palette-jungleGreen200 bg-white px-5 py-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <Label
              htmlFor="fleet-cost-per-month"
              className="text-sm font-semibold text-palette-jungleGreen900"
            >
              Total fleet cost
            </Label>
            <p className="text-xs text-palette-jungleGreen500 mt-0.5">
              Spread evenly across the week: £
              {(configForm.fleet_cost_per_month_gbp / 30).toFixed(2)} added to each day
              (cost ÷ 30)
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm text-palette-jungleGreen500">£</span>
            <Input
              id="fleet-cost-per-month"
              type="number"
              step="0.01"
              min="0"
              value={configForm.fleet_cost_per_month_gbp}
              onChange={(e) =>
                setConfigForm({
                  ...configForm,
                  fleet_cost_per_month_gbp: parseFloat(e.target.value) || 0,
                })
              }
              className="w-36"
            />
            <span className="text-sm text-palette-jungleGreen500">/ month</span>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-palette-jungleGreen200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-palette-jungleGreen900 mb-4">Labour</h3>
          <div className="flex flex-wrap items-end gap-3">
            <NumberField
              id="driver-wage-rate"
              label="Fallback driver wage"
              className="min-w-0 flex-1 space-y-1"
              labelClassName={LABEL}
              inputClassName="w-28"
              step="0.01"
              min="0"
              unit="£/hr"
              hint="Used when a vehicle type has no labour rate set"
              value={configForm.driver_wage_rate_per_hour}
              onChange={(raw) =>
                setConfigForm({
                  ...configForm,
                  driver_wage_rate_per_hour: parseFloat(raw) || 0,
                })
              }
            />
            <NumberField
              id="driver-max-hours"
              label="Driver max hours per day"
              className="min-w-0 flex-1 space-y-1"
              labelClassName={LABEL}
              inputClassName="w-20"
              min="1"
              max="24"
              unit="hours"
              hint={
                <>
                  Used for haulage: total route window (extended sellable time before first stop)
                </>
              }
              value={configForm.driver_max_hours_per_day}
              onChange={(raw) =>
                setConfigForm({
                  ...configForm,
                  driver_max_hours_per_day: Math.min(24, Math.max(1, parseInt(raw, 10) || 12)),
                })
              }
            />
          </div>
        </div>

        <div className="rounded-xl border border-palette-jungleGreen200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-palette-jungleGreen900 mb-4">
            Mileage and Haulage
          </h3>
          <div className="flex flex-wrap items-end gap-3">
            <NumberField
              id="planned-mileage-pct"
              label="Planned mileage add"
              className="min-w-0 flex-1 space-y-1"
              labelClassName={LABEL}
              inputClassName="w-28"
              step="0.5"
              min="0"
              unit="%"
              hint="Added to point-to-point distance to approximate road miles"
              value={configForm.planned_mileage_percentage_add}
              onChange={(raw) =>
                setConfigForm({
                  ...configForm,
                  planned_mileage_percentage_add: parseFloat(raw) || 0,
                })
              }
            />
            <NumberField
              id="haulage-min-empty-pct"
              label="Min empty legs"
              className="min-w-0 flex-1 space-y-1"
              labelClassName={LABEL}
              inputClassName="w-20"
              min="0"
              max="100"
              unit="%"
              hint={
                <>
                  Meaningful empty legs: routes with empty % ≥ this count as haulage applicable
                </>
              }
              value={configForm.haulage_min_empty_pct}
              onChange={(raw) =>
                setConfigForm({
                  ...configForm,
                  haulage_min_empty_pct: Math.min(100, Math.max(0, parseInt(raw, 10) || 10)),
                })
              }
            />
            <NumberField
              id="haulage-min-extended-min"
              label="Min extended start"
              className="min-w-0 flex-1 space-y-1"
              labelClassName={LABEL}
              inputClassName="w-20"
              min="0"
              unit="min"
              hint={
                <>
                  Sizeable extended start: routes with ≥ this many minutes sellable at start
                  count as haulage applicable
                </>
              }
              value={configForm.haulage_min_extended_minutes}
              onChange={(raw) =>
                setConfigForm({
                  ...configForm,
                  haulage_min_extended_minutes: Math.max(0, parseInt(raw, 10) || 60),
                })
              }
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-palette-jungleGreen200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-palette-jungleGreen900 mb-1">Charging rates</h3>
        <p className="text-xs text-palette-jungleGreen500 mb-4">
          Global £/kWh used for route cost. Cost per mile = rate ÷ vehicle Miles per kWh.
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-4">
          <NumberField
            id="on-site-cost-kwh"
            label="On Site (internal)"
            className="min-w-0 flex-1 basis-40 space-y-1"
            labelClassName={LABEL}
            inputClassName="w-24"
            step="0.0001"
            min="0"
            unit="£/kWh"
            value={configForm.on_site_charging_cost_per_kwh}
            onChange={(raw) =>
              setConfigForm({
                ...configForm,
                on_site_charging_cost_per_kwh: parseFloat(raw) || 0,
              })
            }
          />
          <NumberField
            id="oaklands-cost-kwh"
            label="Oaklands (HGV)"
            className="min-w-0 flex-1 basis-40 space-y-1"
            labelClassName={LABEL}
            inputClassName="w-24"
            step="0.001"
            min="0"
            unit="£/kWh"
            hint="All HGV charging; no external"
            value={configForm.oaklands_cost_per_kwh}
            onChange={(raw) => {
              const v = parseFloat(raw)
              setConfigForm({
                ...configForm,
                oaklands_cost_per_kwh: Number.isNaN(v) ? 0.305 : v,
              })
            }}
          />
          <NumberField
            id="default-cost-kwh"
            label="Off Site Fast (external)"
            className="min-w-0 flex-1 basis-40 space-y-1"
            labelClassName={LABEL}
            inputClassName="w-24"
            step="0.0001"
            min="0"
            unit="£/kWh"
            hint="Beyond internal range %"
            value={configForm.default_cost_per_kwh}
            onChange={(raw) =>
              setConfigForm({
                ...configForm,
                default_cost_per_kwh: parseFloat(raw) || 0,
              })
            }
          />
          <NumberField
            id="internal-charging-range-pct"
            label="Range at internal (%)"
            className="min-w-0 flex-1 basis-40 space-y-1"
            labelClassName={LABEL}
            inputClassName="w-24"
            step="1"
            min="0"
            max="100"
            unit="%"
            hint="Of vehicle range at On Site rate"
            value={configForm.internal_charging_range_pct}
            onChange={(raw) => {
              const v = parseFloat(raw)
              setConfigForm({
                ...configForm,
                internal_charging_range_pct: Number.isNaN(v) ? 70 : Math.max(0, Math.min(100, v)),
              })
            }}
          />
        </div>
      </div>

      <div className="rounded-xl border border-palette-jungleGreen200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-palette-jungleGreen900 mb-1">
          UK pallet conversion ratios
        </h3>
        <p className="text-xs text-palette-jungleGreen500 mb-4">
          1 unit = X UK pallets (for load factor)
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-4">
          {ratios.map(({ id, label, value, key }) => (
            <NumberField
              key={key}
              id={id}
              label={label}
              className="min-w-0 flex-1 basis-32 space-y-1"
              labelClassName={LABEL}
              inputClassName="w-20"
              step="0.01"
              min="0.01"
              hint={
                <>
                  1 {label} = {value} UK pallets
                </>
              }
              value={value}
              onChange={(raw) =>
                setConfigForm({
                  ...configForm,
                  [key]: parseFloat(raw) || 0,
                })
              }
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default GlobalConfigTab
